import { faTimes } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { createElement, HTMLAttributes, useRef, useState } from 'react'
import { twMerge } from 'tailwind-merge'
import { useTap } from '../../hooks'
import { Menu, MenuProps } from '../Menu'
import { Modal } from '../Modal'
import { useDebouncedState } from '../../hooks/useDebouncedState'
import { RenderHTMLContent } from './RenderHTMLContent'

type Props<T extends keyof HTMLElementTagNameMap = 'div'> = {
  /** Container tag that surrounds the content that, when hovered, displays the tooltip */
  wrapChildrenWithTag?: T

  /** Delay showing  */
  hoverDelay?: number

  /** Position of the tooltip relative to the Children of this Component */
  position?: MenuProps['position']

  /** This is the text(or element) that is displayed in the tooltip */
  tooltipContent?: React.ReactNode

  /** This is the content that, when hovered, displays the tooltip */
  children: React.ReactNode

  /** These Classes are for the Container of the content that, when hovered, displays the tooltip */
  className?: string

  /** These Classes are Applied to the Popover Menu on Desktop */
  desktopPopoverClasses?: string

  /** These Classes are Applied to the Modal Menu on Mobile */
  mobileModalClasses?: string

  /** Disable tap behavior in the case of wrapping a interactive element (e.g. Button) */
  disableTap?: boolean
} & HTMLAttributes<HTMLElementTagNameMap[T]> &
  React.JSX.IntrinsicElements[T]

export const Tooltip = <T extends keyof HTMLElementTagNameMap = 'div'>({
  tooltipContent,
  position = 'bottom',
  children,
  className,
  wrapChildrenWithTag,
  desktopPopoverClasses,
  disableTap = false,
  ...props
}: Props<T>) => {
  const tagName = wrapChildrenWithTag ?? 'div'

  const [debouncedHovered, setDebouncedHovered] = useDebouncedState(
    false,
    props.hoverDelay ?? 400,
    (value) => value === true,
  )

  const [tapped, setTapped] = useState(false)
  const triggerRef = useRef<HTMLElement>(null)
  useTap(triggerRef, () => setTapped(true), !props.onClick && !disableTap)

  return (
    <>
      {tooltipContent && (
        <>
          <Menu
            isOpen={debouncedHovered}
            onClose={() => setDebouncedHovered(false)}
            closeOnClickOutside={false}
            position={position}
            disablePointerEvents
            className={twMerge('max-w-5xl', desktopPopoverClasses)}
            triggerRef={triggerRef}>
            <RenderHTMLContent>{tooltipContent}</RenderHTMLContent>
          </Menu>
          <Modal
            className='p-8'
            type='modal'
            isOpen={tapped}
            onClose={() => setTapped(false)}>
            <>
              <FontAwesomeIcon
                icon={faTimes}
                onClick={() => setTapped(false)}
                className='absolute text-xl -top-0 -right-0 p-2 text-base-content-muted cursor-pointer'
              />
              <RenderHTMLContent>{tooltipContent}</RenderHTMLContent>
            </>
          </Modal>
        </>
      )}
      {createElement(
        tagName,
        {
          className: twMerge('relative inline-block cursor-pointer', className),
          onPointerEnter: (e: PointerEvent) => {
            e.pointerType === 'mouse' && setDebouncedHovered(true)
          },
          onPointerLeave: (e: PointerEvent) =>
            e.pointerType === 'mouse' && setDebouncedHovered(false),
          ref: triggerRef,
          ...props,
        },
        children,
      )}
    </>
  )
}
